import type { JSX } from 'react';
import { useMemo, useRef, useState, type ComponentType } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Checkbox, Dropdown, Empty, Input, Modal, Popconfirm, Skeleton, type MenuProps } from 'antd';
import {
  AppstoreOutlined,
  BookOutlined,
  BuildOutlined,
  CarOutlined,
  CheckOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  DesktopOutlined,
  DownloadOutlined,
  DownOutlined,
  EditOutlined,
  ExportOutlined,
  FireOutlined,
  FolderAddOutlined,
  FolderOpenOutlined,
  FolderOutlined,
  MoreOutlined,
  PhoneOutlined,
  PictureOutlined,
  QuestionCircleOutlined,
  RightOutlined,
  RiseOutlined,
  RocketOutlined,
  SafetyCertificateOutlined,
  ShareAltOutlined,
  ShopOutlined,
  SoundOutlined,
  SwapOutlined,
  TeamOutlined,
  ToolOutlined,
  UndoOutlined,
  WalletOutlined,
} from '@ant-design/icons';

import { Link } from 'react-router-dom';

import { authApi } from '../lib/authApi';
import { diskApi, type DiskFileDto, type DiskFolderDto, type DiskTrashItemDto } from '../lib/wikiApi';
import { TRASH_RETENTION_DAYS, deleteImpact, diskDeletedMessage, trashDaysLeft, type DeleteImpact } from '../lib/disk-trash';
import { PALETTE_30 } from '../lib/palette';
import { aggregateFileCounts, folderPath } from '../lib/diskFolders';
import { copyDiskFileLink, diskFileMarkdownLink, kbApi } from '../lib/api/kb';
import { Button, PageTitle, fmtInt, plural } from '../components/ds';
import './KnowledgeBase.css';

/**
 * Диск (макет 8 кита «FISHKA HUB»). НАСТОЯЩИЕ папки (отдельная модель
 * WikiDiskFolder/WikiDiskFile) — файлы НЕ привязаны к статьям. Слева ДЕРЕВО
 * папок (вложенность через parentId) с иконкой+цветом+счётчиком, шевроном
 * разворачивания и контекст-меню ⋮ на каждой папке (переименовать / добавить
 * подпапку / сменить иконку / удалить), а также сводкой по объёму. По центру
 * хлебные крошки (Диск › … › выбранная) + таблица файлов выбранной папки с
 * цветными FileType-чипами и контекст-меню ⋮ (переименовать/переместить/
 * скачать/удалить), справа панель выбранного файла.
 *
 * Существующие вложения статей (WikiAttachment) сюда НЕ тянутся — это
 * осознанно отдельное хранилище; вложения остаются на своих статьях.
 *
 * Владелец 07.10.2026: дерево папок растёт по содержимому до высоты окна
 * (липкая колонка, своя прокрутка — только если папок больше, чем влезает);
 * меню «⋮» папок и файлов — antd Dropdown в body (не обрезается колонкой и
 * таблицей); удаление подтверждается окном modal.confirm, а не Popconfirm
 * внутри меню (тот мигал: пункт меню исчезал вместе с меню).
 *
 * Корзина (владелец 22.10.2026): «Удалить» на Диске = в Корзину на 30 дней, оттуда файл можно вернуть
 * или удалить навсегда. Окно удаления заранее говорит, в каких статьях файл используется и что вложение
 * статьи исчезнет. Убрали файл из статьи — он сам уходит сюда с причиной «Убрали из статьи».
 */

/** Окно «Удалить?» — одно на страницу: тексты и кнопки одинаковые везде. */
type ModalApi = ReturnType<typeof App.useApp>['modal'];
function confirmDelete(
  modal: ModalApi,
  opts: { title: string; content: React.ReactNode; okText?: string; onOk: () => Promise<unknown> },
): void {
  modal.confirm({
    title: opts.title,
    content: opts.content,
    okText: opts.okText ?? 'Удалить',
    okButtonProps: { danger: true },
    cancelText: 'Отмена',
    // Enter не должен удалять по ошибке — фокус на «Отмена».
    autoFocusButton: 'cancel',
    // Ошибку показывает onError мутации (текст сервера), окно просто закрываем.
    onOk: () => opts.onOk().catch(() => undefined),
  });
}

/** Подпись статей списком: «А», «Б» и ещё 3. */
function articleList(items: Array<{ title: string }>): string {
  const shown = items.slice(0, 5).map((a) => `«${a.title}»`).join(', ');
  return items.length > 5 ? `${shown} и ещё ${items.length - 5}` : shown;
}

/** Текст окна «Удалить файл?»: Корзина на 30 дней + в каких статьях файл используется. */
function DeleteImpactNote({ impact }: { impact: DeleteImpact }): JSX.Element {
  const att = impact.attachmentArticles.length;
  const lnk = impact.linkedArticles.length;
  return (
    <>
      <div>Файл попадёт в Корзину и пробудет там {TRASH_RETENTION_DAYS} дней — за это время его можно вернуть.</div>
      {att > 0 && (
        <div style={{ marginTop: 8, color: 'var(--fk-text-2)' }}>
          Он прикреплён к {att} {plural(att, 'статье', 'статьям', 'статьям')}: {articleList(impact.attachmentArticles)}. Из «Вложений» статьи он тоже
          исчезнет и при возврате из Корзины в статью не вернётся (вставить его снова можно блоком «Файл с Диска»).
        </div>
      )}
      {lnk > 0 && (
        <div style={{ marginTop: 8, color: 'var(--fk-text-2)' }}>
          Ссылки на файл есть в {lnk} {plural(lnk, 'статье', 'статьях', 'статьях')}: {articleList(impact.linkedArticles)}. Вместо файла там появится
          надпись «Файл удалён».
        </div>
      )}
    </>
  );
}

/** Узел дерева папок: папка + её прямые дети (рекурсивно). */
interface FolderNode {
  folder: DiskFolderDto;
  children: FolderNode[];
}

/** Строит дерево из плоского списка папок по parentId (сохраняя порядок). */
function buildTree(folders: DiskFolderDto[]): FolderNode[] {
  const nodes = new Map<string, FolderNode>();
  for (const f of folders) nodes.set(f.id, { folder: f, children: [] });
  const roots: FolderNode[] = [];
  for (const f of folders) {
    const node = nodes.get(f.id)!;
    const parent = f.parentId ? nodes.get(f.parentId) : null;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  return roots;
}

// ─── Иконки папок (тот же набор, что SECTION_ICONS на лендинге БЗ) ────
const FOLDER_ICONS: Record<string, ComponentType> = {
  folder: FolderOutlined,
  build: BuildOutlined,
  shop: ShopOutlined,
  rise: RiseOutlined,
  sound: SoundOutlined,
  fire: FireOutlined,
  phone: PhoneOutlined,
  wallet: WalletOutlined,
  desktop: DesktopOutlined,
  question: QuestionCircleOutlined,
  rocket: RocketOutlined,
  book: BookOutlined,
  team: TeamOutlined,
  safety: SafetyCertificateOutlined,
  car: CarOutlined,
  picture: PictureOutlined,
  tool: ToolOutlined,
  appstore: AppstoreOutlined,
};
const ICON_CHOICES: string[] = [
  'folder', 'build', 'shop', 'rise', 'sound', 'fire', 'phone', 'wallet',
  'desktop', 'truck', 'safety', 'picture', 'tool', 'book', 'team', 'appstore',
];
// 'truck' нет в наборе — заменяем на car (грузовик/доставка). Маппинг ниже.
function renderFolderIcon(icon: string | null, openState = false): React.ReactNode {
  if (openState) return <FolderOpenOutlined />;
  const key = icon === 'truck' ? 'car' : icon;
  if (key && FOLDER_ICONS[key]) {
    const Ic = FOLDER_ICONS[key];
    return <Ic />;
  }
  return <FolderOutlined />;
}
// Единая палитра из 30 цветов (lib/palette) — общая для пикеров проекта.
const COLOR_CHOICES: string[] = PALETTE_30;

// ─── FileType-чип по расширению (как FileType в ките) ─────────────────
const FILE_COLORS: Record<string, string> = {
  PDF: '#E53935', DOCX: '#1565C0', DOC: '#1565C0', XLSX: '#2E7D32', XLS: '#2E7D32',
  PPTX: '#D9580E', PPT: '#D9580E', AI: '#E0820A', SVG: '#9C5BD4', PNG: '#0096FF',
  JPG: '#0096FF', JPEG: '#0096FF', GIF: '#0096FF', WEBP: '#0096FF',
  MP4: '#7E3FB8', MOV: '#7E3FB8', WEBM: '#7E3FB8', ZIP: '#5B6178', RAR: '#5B6178',
};
function extOf(fileName: string): string {
  const m = /\.([a-z0-9]+)$/i.exec(fileName);
  return (m ? m[1] : '').toUpperCase();
}
function fileColor(fileName: string): string {
  return FILE_COLORS[extOf(fileName)] ?? '#5B6178';
}
function isImage(fileName: string): boolean {
  return ['PNG', 'JPG', 'JPEG', 'GIF', 'WEBP', 'SVG'].includes(extOf(fileName));
}
function FileType({ fileName, size = 34 }: { fileName: string; size?: number }): JSX.Element {
  const ext = extOf(fileName) || 'FILE';
  const c = fileColor(fileName);
  return (
    <span style={{ width: size, height: size, borderRadius: 4, background: c + '1A', color: c, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: size > 34 ? 11 : 9, fontWeight: 800, flexShrink: 0, letterSpacing: '.3px' }}>
      {ext.slice(0, 4)}
    </span>
  );
}
function fmtSize(b: number): string {
  if (b < 1024) return `${b} Б`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} КБ`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} МБ`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} ГБ`;
}


export function DiskPage(): JSX.Element {
  const { message, modal } = App.useApp();
  const qc = useQueryClient();
  const meQuery = useQuery({ queryKey: ['wiki-auth', 'me'], queryFn: () => authApi.me(), retry: false, staleTime: 60_000 });
  const canWrite = (meQuery.data?.canCreate ?? false) || (meQuery.data?.canAdmin ?? false);

  const foldersQuery = useQuery({ queryKey: ['wiki', 'disk', 'folders'], queryFn: () => diskApi.folders(), retry: false });
  const folders = foldersQuery.data ?? [];

  // Дерево папок + первый корень по умолчанию (если ничего не выбрано).
  const tree = useMemo(() => buildTree(folders), [folders]);
  // Карта суммарных счётчиков (с вложенными) — для сайдбара.
  const aggCounts = useMemo(() => aggregateFileCounts(folders), [folders]);
  const [folderId, setFolderId] = useState<string | null>(null);
  // Выбранную папку удалили — откатываемся на первую, а не грузим файлы удалённой.
  const pickedExists = folderId !== null && folders.some((f) => f.id === folderId);
  const activeFolderId = (pickedExists ? folderId : null) ?? tree[0]?.folder.id ?? null;
  const activeFolder = folders.find((f) => f.id === activeFolderId) ?? null;
  const crumbs = useMemo(() => folderPath(folders, activeFolderId), [folders, activeFolderId]);
  // Подпапки активной папки — показываем плиткой в основной области.
  const childFolders = useMemo(
    () => folders.filter((f) => f.parentId === activeFolderId),
    [folders, activeFolderId],
  );

  // Развёрнутые узлы дерева (по id). Раскрываем путь до активной папки.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpand = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const [selected, setSelected] = useState<DiskFileDto | null>(null);
  // Что показываем в основной области: файлы папки или Корзину (только тем, кто может удалять).
  const [view, setView] = useState<'files' | 'trash'>('files');
  const [uploadOpen, setUploadOpen] = useState(false);
  // folderModal: 'new' = новый раздел (top-level); { parentId } = настройки/создание
  // подпапки. Передаём редактируемую папку либо родителя для новой подпапки.
  const [folderModal, setFolderModal] = useState<
    { mode: 'edit'; folder: DiskFolderDto } | { mode: 'create'; parentId: string | null } | null
  >(null);
  // Перемещение: массив файлов (одиночное действие = массив из одного).
  const [moveFiles, setMoveFiles] = useState<DiskFileDto[] | null>(null);
  // Мультивыбор файлов (чекбоксы) для массового перемещения/удаления.
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const clearChecked = () => setCheckedIds(new Set());
  const toggleChecked = (id: string) =>
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selectFolder = (id: string) => {
    setFolderId(id);
    setView('files');
    setSelected(null);
    clearChecked();
    // Авто-раскрытие пути к выбранной папке.
    const path = folderPath(folders, id);
    if (path.length > 1) {
      setExpanded((prev) => {
        const next = new Set(prev);
        for (const p of path.slice(0, -1)) next.add(p.id);
        return next;
      });
    }
  };

  // Корзина: список нужен и для счётчика в меню слева, и для самой панели.
  const trashQuery = useQuery({ queryKey: ['wiki', 'disk', 'trash'], queryFn: () => diskApi.trash(), enabled: canWrite, retry: false, staleTime: 30_000 });
  const trashItems = trashQuery.data ?? [];
  const showTrash = canWrite && view === 'trash';

  const filesQuery = useQuery({
    queryKey: ['wiki', 'disk', 'files', activeFolderId],
    queryFn: () => diskApi.files(activeFolderId!),
    enabled: !!activeFolderId,
    retry: false,
  });
  const files = filesQuery.data ?? [];
  const selectedLive = selected ? (files.find((f) => f.id === selected.id) ?? null) : null;

  const totalSize = useMemo(() => folders.reduce((s, f) => s + f.totalSize, 0), [folders]);
  const totalFiles = useMemo(() => folders.reduce((s, f) => s + f.fileCount, 0), [folders]);
  // Фильтр по имени в текущей папке (клиентский — файлов в папке десятки).
  const [nameFilter, setNameFilter] = useState('');
  const shownFiles = useMemo(() => {
    const q = nameFilter.trim().toLowerCase();
    return q ? files.filter((f) => f.fileName.toLowerCase().includes(q)) : files;
  }, [files, nameFilter]);

  const invalidateAll = async () => {
    await qc.invalidateQueries({ queryKey: ['wiki', 'disk'] });
  };

  // Массовое удаление выбранных файлов (последовательно, чтобы не положить
  // бэк параллельными запросами; ошибки по одному файлу не рушат остальное).
  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      let ok = 0;
      let attachments = 0;
      for (const id of ids) {
        try {
          const r = await diskApi.deleteFile(id);
          ok++;
          if (r.attachmentRemoved) attachments++;
        } catch { /* пропускаем сбойный */ }
      }
      return { ok, total: ids.length, attachments };
    },
    onSuccess: async ({ ok, total, attachments }) => {
      clearChecked();
      setSelected(null);
      await invalidateAll();
      const tail = attachments > 0 ? `, вложения убраны из статей: ${attachments}` : '';
      if (ok === total) message.success(`В Корзине файлов: ${ok} (30 дней)${tail}`);
      else message.warning(`В Корзину ушло ${ok} из ${total} — остальные не удалились, попробуйте ещё раз`);
    },
    onError: (e: Error) => message.error(e.message),
  });

  const askBulkDelete = async (): Promise<void> => {
    const ids = [...checkedIds];
    const n = ids.length;
    // Где файлы используются — спрашиваем заранее (первые 30, чтобы не плодить запросы).
    const usages = await Promise.all(
      ids.slice(0, 30).map((id) =>
        qc.fetchQuery({ queryKey: ['wiki', 'disk', 'usage', id], queryFn: () => kbApi.diskUsage(id), staleTime: 15_000 }).catch(() => null),
      ),
    );
    confirmDelete(modal, {
      title: `Удалить ${n} ${plural(n, 'файл', 'файла', 'файлов')}?`,
      content: <DeleteImpactNote impact={deleteImpact(usages)} />,
      onOk: () => bulkDeleteMutation.mutateAsync(ids),
    });
  };

  if (foldersQuery.isLoading) return <Skeleton active paragraph={{ rows: 8 }} />;
  if (foldersQuery.error) return <Alert type="error" showIcon message="Не удалось загрузить папки" description={(foldersQuery.error as Error).message} />;

  return (
    <div>
      <PageTitle
        title="Диск"
        sub={`${fmtInt(totalFiles)} ${plural(totalFiles, 'файл', 'файла', 'файлов')} · ${folders.length} ${plural(folders.length, 'папка', 'папки', 'папок')} · ${fmtSize(totalSize)}`}
        right={
          canWrite ? (
            <>
              <Button variant="ghost" icon={<FolderAddOutlined />} onClick={() => setFolderModal({ mode: 'create', parentId: null })}>
                Папка
              </Button>
              <Button variant="primary" icon={<CloudUploadOutlined />} onClick={() => setUploadOpen(true)} disabled={!activeFolderId}>
                Загрузить
              </Button>
            </>
          ) : undefined
        }
      />

      <div className="fk-disk-grid">
        {/* Список папок + сводка по объёму. Высота — по содержимому до высоты
            окна (стили .fk-disk-tree ниже), прокрутка — только если папок больше. */}
        <aside className="fk-kb-panel fk-scroll fk-disk-tree" aria-label="Папки диска">
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--fk-text-muted)', textTransform: 'uppercase', letterSpacing: '.5px', padding: '0 10px 8px' }}>Папки</div>
          {tree.length === 0 ? (
            <div style={{ padding: '8px 10px', fontSize: 13, color: 'var(--fk-text-muted)' }}>Папок пока нет</div>
          ) : (
            tree.map((node) => (
              <FolderTreeNode
                key={node.folder.id}
                node={node}
                depth={0}
                aggCounts={aggCounts}
                activeFolderId={activeFolderId}
                expanded={expanded}
                onToggle={toggleExpand}
                onSelect={selectFolder}
                canWrite={canWrite}
                onEdit={(f) => setFolderModal({ mode: 'edit', folder: f })}
                onAddChild={(parentId) => setFolderModal({ mode: 'create', parentId })}
                onChanged={invalidateAll}
              />
            ))
          )}
          {canWrite && (
            <button
              type="button"
              onClick={() => setFolderModal({ mode: 'create', parentId: null })}
              style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '9px 12px', borderRadius: 6, cursor: 'pointer', width: '100%', border: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, color: 'var(--fk-orange)', marginTop: 2 }}
            >
              <FolderAddOutlined /> Новый раздел
            </button>
          )}
          {canWrite && (
            <button
              type="button"
              onClick={() => { setView('trash'); setSelected(null); clearChecked(); }}
              aria-pressed={showTrash}
              title={`Удалённые файлы хранятся ${TRASH_RETENTION_DAYS} дней`}
              style={{
                display: 'flex', alignItems: 'center', gap: 9, padding: '9px 12px', borderRadius: 6, cursor: 'pointer', width: '100%', border: 'none',
                fontFamily: 'inherit', fontSize: 13.5, fontWeight: showTrash ? 700 : 500, marginTop: 6,
                background: showTrash ? 'var(--fk-orange-soft)' : 'transparent', color: showTrash ? 'var(--fk-orange)' : 'var(--fk-text-2)',
              }}
            >
              <DeleteOutlined />
              <span style={{ flex: 1, textAlign: 'left' }}>Корзина</span>
              {trashItems.length > 0 && <span style={{ fontSize: 11, color: 'var(--fk-text-muted)', fontWeight: 600 }}>{trashItems.length}</span>}
            </button>
          )}
          <div style={{ borderTop: '1px solid var(--fk-border)', margin: '10px 0 0', padding: '10px 10px 2px', fontSize: 12, color: 'var(--fk-text-2)', display: 'flex', justifyContent: 'space-between' }}>
            <span>Всего на диске</span>
            <b>{fmtSize(totalSize)}</b>
          </div>
        </aside>

        {showTrash ? (
          <TrashPanel items={trashItems} loading={trashQuery.isLoading} error={trashQuery.error as Error | null} onChanged={invalidateAll} />
        ) : (
        /* Таблица файлов выбранной папки */
        <div className="fk-kb-panel" style={{ minWidth: 0, overflow: 'visible' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '12px 18px', borderBottom: '1px solid var(--fk-border)', fontSize: 13, color: 'var(--fk-text-2)', flexWrap: 'wrap' }}>
            <FolderOpenOutlined style={{ color: 'var(--fk-orange)' }} />
            <button type="button" onClick={() => crumbs[0] && selectFolder(crumbs[0].id)} style={{ border: 'none', background: 'transparent', cursor: crumbs.length ? 'pointer' : 'default', padding: 0, color: 'inherit', fontFamily: 'inherit', fontSize: 13 }}>Диск</button>
            {crumbs.length === 0 ? (
              <><RightOutlined style={{ fontSize: 11 }} /> <span style={{ color: 'var(--fk-text-muted)' }}>—</span></>
            ) : (
              crumbs.map((c, i) => (
                <span key={c.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                  <RightOutlined style={{ fontSize: 11 }} />
                  {i === crumbs.length - 1 ? (
                    <b style={{ color: 'var(--fk-text)' }}>{c.name}</b>
                  ) : (
                    <button type="button" onClick={() => selectFolder(c.id)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, color: 'inherit', fontFamily: 'inherit', fontSize: 13 }}>{c.name}</button>
                  )}
                </span>
              ))
            )}
            <span style={{ flex: 1 }} />
            {files.length > 5 && (
              <input
                className="fk-kb-select"
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                placeholder="Фильтр по имени"
                aria-label="Фильтр по имени"
                style={{ width: 180 }}
              />
            )}
            <span style={{ color: 'var(--fk-text-muted)' }}>
              {shownFiles.length} {plural(shownFiles.length, 'файл', 'файла', 'файлов')}
            </span>
          </div>

          {/* Панель массовых действий — появляется когда выбран ≥1 файл. */}
          {canWrite && checkedIds.size > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 18px', borderBottom: '1px solid var(--fk-border)', background: 'var(--fk-orange-soft)', flexWrap: 'wrap' }}>
              <b style={{ fontSize: 13, color: 'var(--fk-orange)' }}>Выбрано: {checkedIds.size}</b>
              <button
                type="button"
                className="fk-btn fk-btn-ghost fk-btn-sm"
                disabled={folders.length < 2}
                onClick={() => setMoveFiles(files.filter((f) => checkedIds.has(f.id)))}
              >
                <SwapOutlined /> Переместить
              </button>
              <button
                type="button"
                className="fk-btn fk-btn-ghost fk-btn-sm"
                style={{ color: 'var(--fk-error)' }}
                disabled={bulkDeleteMutation.isPending}
                onClick={() => void askBulkDelete()}
              >
                <DeleteOutlined /> Удалить
              </button>
              <button type="button" className="fk-btn fk-btn-ghost fk-btn-sm" style={{ marginLeft: 'auto' }} onClick={clearChecked}>
                Снять выделение
              </button>
            </div>
          )}
          {/* Плитка подпапок — как в проводнике. Показываем всегда, когда у
              папки есть дети (и сверху файлов, если они тоже есть). */}
          {activeFolderId && childFolders.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, padding: '16px 18px' }}>
              {childFolders.map((cf) => (
                <button
                  key={cf.id}
                  type="button"
                  onClick={() => selectFolder(cf.id)}
                  title={cf.name}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 11, padding: '13px 14px', borderRadius: 'var(--fk-radius-md)',
                    border: '1px solid var(--fk-border)', background: 'var(--fk-surface)', cursor: 'pointer',
                    fontFamily: 'inherit', textAlign: 'left', transition: 'all 0.12s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--fk-orange)'; e.currentTarget.style.background = 'var(--fk-orange-soft)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--fk-border)'; e.currentTarget.style.background = 'var(--fk-surface)'; }}
                >
                  <span style={{ width: 38, height: 38, borderRadius: 6, background: cf.color + '1A', color: cf.color, display: 'grid', placeItems: 'center', fontSize: 18, flexShrink: 0 }}>
                    {renderFolderIcon(cf.icon)}
                  </span>
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--fk-text)' }}>{cf.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--fk-text-muted)' }}>{aggCounts.get(cf.id) ?? cf.fileCount} файлов</div>
                  </span>
                </button>
              ))}
            </div>
          )}
          {!activeFolderId ? (
            <div style={{ padding: 40 }}><Empty description="Создайте папку, чтобы загружать файлы" /></div>
          ) : filesQuery.isLoading ? (
            <div style={{ padding: 24 }}><Skeleton active paragraph={{ rows: 4 }} /></div>
          ) : files.length === 0 ? (
            childFolders.length > 0 ? (
              // Файлов нет, но есть подпапки — не пишем «пусто», плитка выше уже
              // показывает содержимое; даём только кнопку загрузки.
              canWrite ? (
                <div style={{ padding: '0 18px 18px', textAlign: 'center' }}>
                  <button type="button" className="fk-btn fk-btn-ghost fk-btn-sm" onClick={() => setUploadOpen(true)}><CloudUploadOutlined /> Загрузить файл сюда</button>
                </div>
              ) : null
            ) : (
              <div style={{ padding: '50px 20px', textAlign: 'center', color: 'var(--fk-text-muted)' }}>
                <FolderOpenOutlined style={{ fontSize: 30 }} />
                <div style={{ fontWeight: 600, marginTop: 10, fontSize: 14 }}>Папка пуста</div>
                {canWrite && <button type="button" className="fk-btn fk-btn-primary fk-btn-sm" style={{ marginTop: 14 }} onClick={() => setUploadOpen(true)}><CloudUploadOutlined /> Загрузить файл</button>}
              </div>
            )
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 560 }}>
                <thead>
                  <tr style={{ background: 'var(--fk-bg-subtle)' }}>
                    {canWrite && (
                      <th style={{ ...th, width: 40, paddingRight: 0 }}>
                        <Checkbox
                          checked={shownFiles.length > 0 && shownFiles.every((f) => checkedIds.has(f.id))}
                          indeterminate={shownFiles.some((f) => checkedIds.has(f.id)) && !shownFiles.every((f) => checkedIds.has(f.id))}
                          onChange={(e) =>
                            setCheckedIds(e.target.checked ? new Set(shownFiles.map((f) => f.id)) : new Set())
                          }
                        />
                      </th>
                    )}
                    <th style={th}>Имя файла</th>
                    <th style={{ ...th, width: 90 }}>Размер</th>
                    <th style={{ ...th, width: 120 }}>Обновлён</th>
                    {canWrite && <th style={{ ...th, width: 44 }} />}
                  </tr>
                </thead>
                <tbody>
                  {shownFiles.map((f) => (
                    <FileRow
                      key={f.id}
                      file={f}
                      selected={selected?.id === f.id}
                      checked={checkedIds.has(f.id)}
                      onToggleCheck={() => toggleChecked(f.id)}
                      canWrite={canWrite}
                      folders={folders}
                      onSelect={() => setSelected(f)}
                      onMove={() => setMoveFiles([f])}
                      onChanged={invalidateAll}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        )}

        {/* Панель выбранного файла */}
        {/* Берём файл из свежего списка: удалили или переместили — панель
            закрывается, переименовали — показывает новое имя. */}
        {!showTrash && selectedLive && <FilePreview file={selectedLive} onClose={() => setSelected(null)} />}
      </div>

      {uploadOpen && activeFolderId && (
        <UploadModal folderId={activeFolderId} folderName={activeFolder?.name ?? ''} onClose={() => setUploadOpen(false)} onDone={invalidateAll} />
      )}
      {folderModal && (
        <FolderModal
          folder={folderModal.mode === 'edit' ? folderModal.folder : null}
          parentId={folderModal.mode === 'create' ? folderModal.parentId : null}
          parentName={folderModal.mode === 'create' && folderModal.parentId ? (folders.find((f) => f.id === folderModal.parentId)?.name ?? null) : null}
          onClose={() => setFolderModal(null)}
          onDone={invalidateAll}
        />
      )}
      {moveFiles && moveFiles.length > 0 && (
        <MoveModal
          files={moveFiles}
          folders={folders}
          onClose={() => setMoveFiles(null)}
          onDone={async () => { clearChecked(); await invalidateAll(); }}
        />
      )}

      <style>{`
        .fk-disk-grid { display: grid; grid-template-columns: 1fr; gap: 18px; align-items: start; }
        /* Телефон и узкое окно: дерево полной высоты, без липкости и своей прокрутки. */
        .fk-disk-tree { padding: 10px; }
        @media (min-width: 1100px) {
          .fk-disk-grid { grid-template-columns: 260px minmax(0,1fr); grid-auto-columns: 300px; grid-auto-flow: column; }
          /* Липкая колонка под шапкой: растёт по содержимому до высоты окна,
             своя прокрутка появляется, только если папок больше, чем влезает. */
          .fk-disk-tree {
            position: sticky; top: calc(var(--fk-hub-header-h) + 16px);
            max-height: calc(100vh - var(--fk-hub-header-h) - 32px);
            overflow-y: auto; overscroll-behavior: contain;
          }
        }
      `}</style>
    </div>
  );
}

// ─── Панель выбранного файла (HUB 2.0) ───────────────────────────────
/**
 * Превью + «Скачать», «Ссылка», «В статью» (копирует ссылку в буфер как HTML и
 * Markdown сразу — в редакторе статьи она станет карточкой файла или ссылкой) и
 * «Используется в N статьях» (статьи, где есть ссылка на файл; с ACL вьюера).
 */
function FilePreview({ file, onClose }: { file: DiskFileDto; onClose: () => void }): JSX.Element {
  const { message } = App.useApp();
  const usage = useQuery({ queryKey: ['wiki', 'disk', 'usage', file.id], queryFn: () => kbApi.diskUsage(file.id), retry: false, staleTime: 60_000 });
  const copy = (text: string, ok: string): void => {
    navigator.clipboard?.writeText(text).then(
      () => message.success(ok),
      () => message.info(text),
    );
  };
  const n = usage.data?.count ?? 0;
  const attachmentOf = usage.data?.attachmentOf ?? null;
  return (
    <aside className="fk-kb-panel fk-kb-disk-preview">
      <div className="fk-kb-disk-thumb" style={{ background: fileColor(file.fileName) + '12', position: 'relative' }}>
        {isImage(file.fileName) ? (
          <img src={diskApi.downloadUrl(file.id)} alt={file.fileName} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
        ) : (
          <FileType fileName={file.fileName} size={56} />
        )}
        <button type="button" className="fk-kb-icon-btn" onClick={onClose} title="Закрыть" style={{ position: 'absolute', top: 8, right: 8 }}>
          ×
        </button>
      </div>
      <div>
        <div style={{ fontWeight: 700, lineHeight: 1.3, wordBreak: 'break-word' }}>{file.fileName}</div>
        <div style={{ fontSize: 12, color: 'var(--fk-text-muted)', marginTop: 4 }}>
          {[extOf(file.fileName) || 'FILE', fmtSize(file.sizeBytes), new Date(file.updatedAt ?? file.createdAt).toLocaleDateString('ru-RU'), file.uploadedByName]
            .filter(Boolean)
            .join(' · ')}
        </div>
      </div>
      <Button variant="primary" block icon={<DownloadOutlined />} href={diskApi.downloadUrl(file.id)}>
        Скачать
      </Button>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button
          variant="ghost"
          size="sm"
          icon={<ShareAltOutlined />}
          style={{ flex: 1 }}
          onClick={() => copy(new URL(diskApi.downloadUrl(file.id), window.location.origin).href, 'Ссылка скопирована')}
        >
          Ссылка
        </Button>
        <Button
          variant="ghost"
          size="sm"
          icon={<ExportOutlined />}
          style={{ flex: 1 }}
          title="Скопировать ссылку для вставки в текст статьи"
          onClick={() => {
            // В буфер — и ссылка (text/html), и markdown (text/plain): редактор вставит настоящую ссылку/карточку.
            void copyDiskFileLink(file).then((ok) => {
              if (ok) message.success('Ссылка для статьи скопирована — вставьте её в редакторе (Ctrl+V)');
              else message.info(diskFileMarkdownLink(file));
            });
          }}
        >
          В статью
        </Button>
      </div>
      {attachmentOf && (
        <div style={{ fontSize: 12, color: 'var(--fk-text-2)', borderTop: '1px solid var(--fk-row-line)', paddingTop: 10 }}>
          Вложение статьи:{' '}
          <Link to={`/article/${attachmentOf.slug}`} style={{ fontWeight: 600 }}>
            {attachmentOf.title}
          </Link>
        </div>
      )}
      <div style={{ fontSize: 12, color: 'var(--fk-text-2)', borderTop: '1px solid var(--fk-row-line)', paddingTop: 10 }}>
        {usage.isLoading ? (
          'Ищем, где используется…'
        ) : n === 0 ? (
          'Пока не используется в статьях.'
        ) : (
          <>
            Используется в {n} {plural(n, 'статье', 'статьях', 'статьях')}:
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
              {(usage.data?.articles ?? []).slice(0, 5).map((a) => (
                <Link key={a.slug} to={`/article/${a.slug}`} style={{ fontWeight: 600 }}>
                  {a.title}
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </aside>
  );
}

// ─── Узел дерева папок (рекурсивный) ─────────────────────────────────
function FolderTreeNode({
  node, depth, aggCounts, activeFolderId, expanded, onToggle, onSelect, canWrite, onEdit, onAddChild, onChanged,
}: {
  node: FolderNode;
  depth: number;
  aggCounts: Map<string, number>;
  activeFolderId: string | null;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  onSelect: (id: string) => void;
  canWrite: boolean;
  onEdit: (f: DiskFolderDto) => void;
  onAddChild: (parentId: string) => void;
  onChanged: () => Promise<void>;
}): JSX.Element {
  const { folder, children } = node;
  const active = folder.id === activeFolderId;
  const hasChildren = children.length > 0;
  const isOpen = expanded.has(folder.id);
  const { message, modal } = App.useApp();

  const deleteMutation = useMutation({
    mutationFn: () => diskApi.deleteFolder(folder.id),
    onSuccess: async () => { await onChanged(); message.success('Папка удалена'); },
    // Текст от сервера: «В папке 3 файл(ов) — сначала переместите или удалите их».
    onError: (e: Error) => message.error(e.message),
  });

  // Бэкенд удаляет только пустую папку (wiki-disk.service deleteFolder):
  // без своих файлов и без подпапок. Если видим, что внутри что-то есть, —
  // говорим об этом сразу; окончательно решает сервер.
  const askDelete = (): void => {
    const own = folder.fileCount;
    const subs = children.length;
    const inside = [
      own > 0 ? `${own} ${plural(own, 'файл', 'файла', 'файлов')}` : null,
      subs > 0 ? `${subs} ${plural(subs, 'подпапка', 'подпапки', 'подпапок')}` : null,
    ].filter(Boolean).join(' и ');
    confirmDelete(modal, {
      title: `Удалить папку «${folder.name}»?`,
      content: (
        <>
          <div>Удалить можно только пустую папку — без файлов и подпапок.</div>
          {inside && <div style={{ marginTop: 8, color: 'var(--fk-text-2)' }}>Сейчас в ней {inside} — сначала перенесите или удалите их.</div>}
        </>
      ),
      onOk: () => deleteMutation.mutateAsync(),
    });
  };

  const menuItems: MenuProps['items'] = [
    { key: 'rename', icon: <EditOutlined />, label: 'Переименовать' },
    { key: 'add', icon: <FolderAddOutlined />, label: 'Добавить подпапку' },
    { key: 'icon', icon: <PictureOutlined />, label: 'Сменить иконку' },
    { type: 'divider' },
    { key: 'delete', icon: <DeleteOutlined />, label: 'Удалить папку', danger: true },
  ];
  const onMenu: MenuProps['onClick'] = ({ key }) => {
    if (key === 'rename' || key === 'icon') onEdit(folder);
    else if (key === 'add') onAddChild(folder.id);
    else if (key === 'delete') askDelete();
  };

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={() => onSelect(folder.id)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onSelect(folder.id); }}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 6, padding: '9px 8px', borderRadius: 6,
          cursor: 'pointer', background: active ? 'var(--fk-orange-soft)' : 'transparent', marginBottom: 1,
          color: active ? 'var(--fk-orange)' : 'var(--fk-text-2)', fontWeight: active ? 700 : 500, fontSize: 13.5,
          transition: 'background 0.12s', paddingLeft: 8 + depth * 16,
        }}
        onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'var(--fk-bg-subtle)'; }}
        onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
      >
        {/* Шеврон разворачивания — только если есть дети */}
        <span
          onClick={(e) => { if (hasChildren) { e.stopPropagation(); onToggle(folder.id); } }}
          style={{ width: 16, display: 'inline-flex', justifyContent: 'center', flexShrink: 0, color: 'var(--fk-text-muted)', cursor: hasChildren ? 'pointer' : 'default' }}
        >
          {hasChildren && (isOpen ? <DownOutlined style={{ fontSize: 10 }} /> : <RightOutlined style={{ fontSize: 10 }} />)}
        </span>
        <span style={{ color: active ? 'var(--fk-orange)' : folder.color, display: 'inline-flex', flexShrink: 0 }}>
          {renderFolderIcon(folder.icon, active && !hasChildren)}
        </span>
        <span title={folder.name} style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{folder.name}</span>
        <span
          title={`Файлов всего (с вложенными): ${aggCounts.get(folder.id) ?? folder.fileCount}`}
          style={{ fontSize: 11, color: 'var(--fk-text-muted)', fontWeight: 600 }}
        >
          {aggCounts.get(folder.id) ?? folder.fileCount}
        </span>
        {canWrite && (
          // Меню рендерится в body, но события React всплывают по дереву
          // компонентов: клик и Enter в меню не должны выбирать папку.
          <span
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation(); }}
          >
            <Dropdown
              trigger={['click']}
              placement="bottomRight"
              getPopupContainer={() => document.body}
              menu={{ items: menuItems, onClick: onMenu }}
            >
              <button
                type="button"
                title="Действия с папкой"
                aria-label={`Действия с папкой «${folder.name}»`}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--fk-text-muted)', padding: 2, display: 'inline-flex' }}
              >
                <MoreOutlined style={{ fontSize: 14 }} />
              </button>
            </Dropdown>
          </span>
        )}
      </div>
      {hasChildren && isOpen && children.map((child) => (
        <FolderTreeNode
          key={child.folder.id}
          node={child}
          depth={depth + 1}
          aggCounts={aggCounts}
          activeFolderId={activeFolderId}
          expanded={expanded}
          onToggle={onToggle}
          onSelect={onSelect}
          canWrite={canWrite}
          onEdit={onEdit}
          onAddChild={onAddChild}
          onChanged={onChanged}
        />
      ))}
    </>
  );
}

// ─── Строка файла + контекст-меню ⋮ ──────────────────────────────────
function FileRow({ file, selected, checked, onToggleCheck, canWrite, folders, onSelect, onMove, onChanged }: {
  file: DiskFileDto; selected: boolean; checked: boolean; onToggleCheck: () => void;
  canWrite: boolean; folders: DiskFolderDto[];
  onSelect: () => void; onMove: () => void; onChanged: () => Promise<void>;
}): JSX.Element {
  const { message, modal } = App.useApp();
  const qc = useQueryClient();
  const [renaming, setRenaming] = useState(false);
  const [renameVal, setRenameVal] = useState(file.fileName);

  const renameMutation = useMutation({
    mutationFn: (name: string) => diskApi.updateFile(file.id, { fileName: name }),
    onSuccess: async () => { setRenaming(false); await onChanged(); message.success('Переименовано'); },
    onError: (e: Error) => message.error(e.message),
  });
  const deleteMutation = useMutation({
    mutationFn: () => diskApi.deleteFile(file.id),
    onSuccess: async (r) => { await onChanged(); message.success(diskDeletedMessage(r)); },
    onError: (e: Error) => message.error(e.message),
  });

  // Перед удалением узнаём, в каких статьях файл используется, и говорим об этом в окне.
  const askDelete = async (): Promise<void> => {
    const usage = await qc
      .fetchQuery({ queryKey: ['wiki', 'disk', 'usage', file.id], queryFn: () => kbApi.diskUsage(file.id), staleTime: 15_000 })
      .catch(() => null);
    confirmDelete(modal, {
      title: `Удалить файл «${file.fileName}»?`,
      content: <DeleteImpactNote impact={deleteImpact([usage])} />,
      onOk: () => deleteMutation.mutateAsync(),
    });
  };

  const commitRename = () => {
    const v = renameVal.trim();
    if (!v || v === file.fileName) { setRenaming(false); return; }
    renameMutation.mutate(v);
  };

  // Как прежняя ссылка <a download>: файл скачивается, страница остаётся
  // (работает и с клавиатуры — Enter на пункте меню).
  const download = (): void => {
    const a = document.createElement('a');
    a.href = diskApi.downloadUrl(file.id);
    a.download = file.fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const menuItems: MenuProps['items'] = [
    { key: 'rename', icon: <EditOutlined />, label: 'Переименовать' },
    { key: 'move', icon: <SwapOutlined />, label: 'Переместить', disabled: folders.length < 2 },
    { key: 'download', icon: <DownloadOutlined />, label: 'Скачать' },
    { type: 'divider' },
    { key: 'delete', icon: <DeleteOutlined />, label: 'Удалить', danger: true },
  ];
  const onMenu: MenuProps['onClick'] = ({ key }) => {
    if (key === 'rename') { setRenameVal(file.fileName); setRenaming(true); }
    else if (key === 'move') onMove();
    else if (key === 'download') download();
    else if (key === 'delete') void askDelete();
  };

  return (
    <tr
      onClick={onSelect}
      style={{ borderTop: '1px solid var(--fk-border)', cursor: 'pointer', background: selected ? 'var(--fk-orange-soft)' : 'transparent', transition: 'background 0.12s' }}
      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.background = 'var(--fk-bg-subtle)'; }}
      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = 'transparent'; }}
    >
      {canWrite && (
        <td style={{ ...td, paddingRight: 0 }} onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={checked} onChange={onToggleCheck} />
        </td>
      )}
      <td style={td}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
          <FileType fileName={file.fileName} />
          {renaming ? (
            <Input
              autoFocus
              size="small"
              value={renameVal}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => setRenameVal(e.target.value)}
              onBlur={commitRename}
              onPressEnter={commitRename}
              style={{ width: 220 }}
            />
          ) : (
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.fileName}</div>
              {file.uploadedByName && <div style={{ fontSize: 11.5, color: 'var(--fk-text-muted)' }}>{file.uploadedByName}</div>}
            </div>
          )}
        </div>
      </td>
      <td style={{ ...td, fontSize: 13, color: 'var(--fk-text-2)' }}>{fmtSize(file.sizeBytes)}</td>
      <td style={{ ...td, fontSize: 13, color: 'var(--fk-text-2)' }}>{new Date(file.updatedAt).toLocaleDateString('ru-RU')}</td>
      {canWrite && (
        // Меню — в body: таблица с горизонтальной прокруткой его больше не
        // обрезает. Клики в меню не выбирают строку (stopPropagation на ячейке).
        <td style={td} onClick={(e) => e.stopPropagation()}>
          <Dropdown
            trigger={['click']}
            placement="bottomRight"
            getPopupContainer={() => document.body}
            menu={{ items: menuItems, onClick: onMenu }}
          >
            <button
              type="button"
              title="Действия"
              aria-label={`Действия с файлом «${file.fileName}»`}
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--fk-text-2)', padding: 6, borderRadius: 6, display: 'inline-flex' }}
            >
              <MoreOutlined />
            </button>
          </Dropdown>
        </td>
      )}
    </tr>
  );
}


// ─── Корзина: удалённое за последние 30 дней ─────────────────────────
function TrashPanel({ items, loading, error, onChanged }: {
  items: DiskTrashItemDto[]; loading: boolean; error: Error | null; onChanged: () => Promise<void>;
}): JSX.Element {
  const { message, modal } = App.useApp();
  const restoreMutation = useMutation({
    mutationFn: (id: string) => diskApi.restoreFile(id),
    onSuccess: async (f) => { await onChanged(); message.success(`Файл «${f.fileName}» снова на Диске`); },
    onError: (e: Error) => message.error(e.message),
  });
  const purgeMutation = useMutation({
    mutationFn: (id: string) => diskApi.purgeFile(id),
    onSuccess: async () => { await onChanged(); message.success('Файл удалён навсегда'); },
    onError: (e: Error) => message.error(e.message),
  });
  const askPurge = (f: DiskTrashItemDto): void => {
    confirmDelete(modal, {
      title: `Удалить «${f.fileName}» навсегда?`,
      content: 'Файл исчезнет сразу и без возможности восстановления.',
      okText: 'Удалить навсегда',
      onOk: () => purgeMutation.mutateAsync(f.id),
    });
  };

  return (
    <div className="fk-kb-panel" style={{ minWidth: 0, overflow: 'visible' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 18px', borderBottom: '1px solid var(--fk-border)', fontSize: 13, color: 'var(--fk-text-2)', flexWrap: 'wrap' }}>
        <DeleteOutlined style={{ color: 'var(--fk-orange)' }} />
        <b style={{ color: 'var(--fk-text)' }}>Корзина</b>
        <span style={{ color: 'var(--fk-text-muted)' }}>
          Удалённые файлы хранятся {TRASH_RETENTION_DAYS} дней, потом исчезают окончательно.
        </span>
        <span style={{ flex: 1 }} />
        <span style={{ color: 'var(--fk-text-muted)' }}>{items.length} {plural(items.length, 'файл', 'файла', 'файлов')}</span>
      </div>
      {error ? (
        <div style={{ padding: 24 }}><Alert type="error" showIcon message="Не удалось загрузить Корзину" description={error.message} /></div>
      ) : loading ? (
        <div style={{ padding: 24 }}><Skeleton active paragraph={{ rows: 4 }} /></div>
      ) : items.length === 0 ? (
        <div style={{ padding: '50px 20px', textAlign: 'center', color: 'var(--fk-text-muted)' }}>
          <DeleteOutlined style={{ fontSize: 30 }} />
          <div style={{ fontWeight: 600, marginTop: 10, fontSize: 14 }}>Корзина пуста</div>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 760 }}>
            <thead>
              <tr style={{ background: 'var(--fk-bg-subtle)' }}>
                <th style={th}>Файл</th>
                <th style={th}>Папка</th>
                <th style={th}>Кто и когда удалил</th>
                <th style={th}>Причина</th>
                <th style={{ ...th, width: 110 }}>Осталось</th>
                <th style={{ ...th, width: 250 }} />
              </tr>
            </thead>
            <tbody>
              {items.map((f) => {
                const left = trashDaysLeft(f.expiresAt);
                return (
                  <tr key={f.id} style={{ borderTop: '1px solid var(--fk-border)' }}>
                    <td style={td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
                        <FileType fileName={f.fileName} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260 }} title={f.fileName}>{f.fileName}</div>
                          <div style={{ fontSize: 11.5, color: 'var(--fk-text-muted)' }}>{fmtSize(f.sizeBytes)}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ ...td, fontSize: 13, color: 'var(--fk-text-2)' }}>{f.folderPath || '—'}</td>
                    <td style={{ ...td, fontSize: 13, color: 'var(--fk-text-2)' }}>
                      <div>{f.deletedByName ?? 'Система'}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--fk-text-muted)' }}>{new Date(f.deletedAt).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' })}</div>
                    </td>
                    <td style={{ ...td, fontSize: 13, color: 'var(--fk-text-2)' }}>{f.reasonLabel}</td>
                    <td style={{ ...td, fontSize: 13, color: left <= 3 ? 'var(--fk-error)' : 'var(--fk-text-2)' }}>
                      {left} {plural(left, 'день', 'дня', 'дней')}
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: 8 }}>
                        <button
                          type="button"
                          className="fk-btn fk-btn-ghost fk-btn-sm"
                          disabled={restoreMutation.isPending}
                          onClick={() => restoreMutation.mutate(f.id)}
                        >
                          <UndoOutlined /> Восстановить
                        </button>
                        <button
                          type="button"
                          className="fk-btn fk-btn-ghost fk-btn-sm"
                          style={{ color: 'var(--fk-error)' }}
                          disabled={purgeMutation.isPending}
                          onClick={() => askPurge(f)}
                        >
                          <DeleteOutlined /> Навсегда
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const th: React.CSSProperties = { textAlign: 'left', padding: '11px 16px', fontWeight: 700, fontSize: 12, color: 'var(--fk-text-muted)', textTransform: 'uppercase', letterSpacing: '.3px' };
const td: React.CSSProperties = { padding: '12px 16px', verticalAlign: 'middle' };

// ─── Модалка создания/редактирования папки ───────────────────────────
function FolderModal({ folder, parentId, parentName, onClose, onDone }: {
  folder: DiskFolderDto | null;
  parentId: string | null;
  parentName: string | null;
  onClose: () => void;
  onDone: () => Promise<void>;
}): JSX.Element {
  const { message } = App.useApp();
  const [name, setName] = useState(folder?.name ?? '');
  const [icon, setIcon] = useState<string>(folder?.icon ?? 'folder');
  const [color, setColor] = useState<string>(folder?.color ?? '#251D4A');

  const saveMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      if (folder) {
        await diskApi.updateFolder(folder.id, { name: name.trim(), icon, color });
      } else {
        await diskApi.createFolder({ name: name.trim(), icon, color, parentId });
      }
    },
    onSuccess: async () => { await onDone(); message.success(folder ? 'Папка обновлена' : 'Папка создана'); onClose(); },
    onError: (e: Error) => message.error(e.message),
  });
  const deleteMutation = useMutation({
    mutationFn: () => diskApi.deleteFolder(folder!.id),
    onSuccess: async () => { await onDone(); message.success('Папка удалена'); onClose(); },
    onError: (e: Error) => message.error(e.message),
  });

  return (
    <Modal
      open
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <span style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--fk-orange-soft)', color: 'var(--fk-orange)', display: 'grid', placeItems: 'center' }}><FolderAddOutlined /></span>
          {folder ? 'Настройки папки' : parentId ? 'Новая подпапка' : 'Новый раздел'}
        </span>
      }
      onCancel={onClose}
      okText={folder ? 'Сохранить' : 'Создать'}
      okButtonProps={{ disabled: !name.trim(), loading: saveMutation.isPending, style: { background: 'var(--fk-orange)', borderColor: 'var(--fk-orange)' } }}
      onOk={() => saveMutation.mutate()}
      footer={folder ? [
        // Обычная кнопка в подвале окна (не пункт меню) — Popconfirm здесь не мигает.
        <Popconfirm
          key="del"
          title={`Удалить папку «${folder.name}»?`}
          description="Удалить можно только пустую папку — без файлов и подпапок"
          okText="Удалить"
          cancelText="Отмена"
          okButtonProps={{ danger: true, loading: deleteMutation.isPending }}
          onConfirm={() => deleteMutation.mutate()}
        >
          <button type="button" className="fk-btn fk-btn-ghost" style={{ marginRight: 'auto', color: 'var(--fk-error, #E53935)' }}>
            <DeleteOutlined /> Удалить
          </button>
        </Popconfirm>,
        <button key="cancel" type="button" className="fk-btn fk-btn-ghost" onClick={onClose}>Отмена</button>,
        <button key="ok" type="button" className="fk-btn fk-btn-primary" disabled={!name.trim()} onClick={() => saveMutation.mutate()}>
          <CheckOutlined /> Сохранить
        </button>,
      ] : undefined}
    >
      {!folder && parentName && (
        <div style={{ fontSize: 12.5, color: 'var(--fk-text-2)', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <FolderOutlined style={{ color: 'var(--fk-orange)' }} /> Внутри: <b>{parentName}</b>
        </div>
      )}
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--fk-text-muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>Название</div>
      <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Например: Сезонное меню" style={{ marginBottom: 16 }} onPressEnter={() => name.trim() && saveMutation.mutate()} />

      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--fk-text-muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>Иконка</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
        {ICON_CHOICES.map((key) => {
          const active = key === icon;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setIcon(key)}
              style={{ width: 42, height: 42, borderRadius: 6, cursor: 'pointer', display: 'grid', placeItems: 'center', fontSize: 17, border: '1.5px solid ' + (active ? 'var(--fk-orange)' : 'var(--fk-border)'), background: active ? 'var(--fk-orange-soft)' : 'var(--fk-surface)', color: active ? 'var(--fk-orange)' : 'var(--fk-text-2)' }}
            >
              {renderFolderIcon(key)}
            </button>
          );
        })}
      </div>

      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--fk-text-muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>Цвет</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {COLOR_CHOICES.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setColor(c)}
            title={c}
            style={{ width: 30, height: 30, borderRadius: 8, cursor: 'pointer', background: c, border: '2px solid ' + (c === color ? 'var(--fk-text)' : 'transparent'), display: 'grid', placeItems: 'center', color: '#fff' }}
          >
            {c === color && <CheckOutlined style={{ fontSize: 13 }} />}
          </button>
        ))}
      </div>
    </Modal>
  );
}

// ─── Модалка перемещения файла ───────────────────────────────────────
function MoveModal({ files, folders, onClose, onDone }: { files: DiskFileDto[]; folders: DiskFolderDto[]; onClose: () => void; onDone: () => Promise<void> }): JSX.Element {
  const { message } = App.useApp();
  const moveMutation = useMutation({
    mutationFn: async (targetId: string) => {
      for (const f of files) {
        try { await diskApi.updateFile(f.id, { folderId: targetId }); } catch { /* пропускаем сбойный */ }
      }
    },
    onSuccess: async () => { await onDone(); message.success(files.length > 1 ? `Перемещено файлов: ${files.length}` : 'Файл перемещён'); onClose(); },
    onError: (e: Error) => message.error(e.message),
  });
  // Папка-источник: если все файлы из одной папки — прячем её из списка целей.
  const srcFolderId = files.every((f) => f.folderId === files[0].folderId) ? files[0].folderId : null;

  return (
    <Modal
      open
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <span style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--fk-orange-soft)', color: 'var(--fk-orange)', display: 'grid', placeItems: 'center' }}><SwapOutlined /></span>
          Переместить файл
        </span>
      }
      onCancel={onClose}
      footer={null}
    >
      <div style={{ fontSize: 13, color: 'var(--fk-text-2)', marginBottom: 12 }}>
        {files.length > 1 ? <>Выберите папку для <b>{files.length} файлов</b></> : <>Выберите папку для <b>{files[0].fileName}</b></>}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 320, overflowY: 'auto' }}>
        {folders.filter((f) => f.id !== srcFolderId).map((f) => {
          // Полный путь до папки в подписи (Маркетинг › Акции › Лето 2026).
          const path = folderPath(folders, f.id);
          const crumbLabel = path.length > 1 ? path.slice(0, -1).map((p) => p.name).join(' › ') : null;
          return (
          <button
            key={f.id}
            type="button"
            disabled={moveMutation.isPending}
            onClick={() => moveMutation.mutate(f.id)}
            style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 13px', borderRadius: 'var(--fk-radius-md)', border: '1px solid var(--fk-border)', background: 'var(--fk-surface)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, transition: 'all 0.12s' }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--fk-orange)'; e.currentTarget.style.background = 'var(--fk-orange-soft)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--fk-border)'; e.currentTarget.style.background = 'var(--fk-surface)'; }}
          >
            <span style={{ color: f.color, display: 'inline-flex' }}>{renderFolderIcon(f.icon)}</span>
            <span style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
              {crumbLabel && <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--fk-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{crumbLabel}</div>}
              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
            </span>
            <RightOutlined style={{ fontSize: 12, color: 'var(--fk-text-muted)' }} />
          </button>
          );
        })}
        {folders.filter((f) => f.id !== srcFolderId).length === 0 && (
          <div style={{ fontSize: 13, color: 'var(--fk-text-muted)', padding: 8 }}>Нет других папок для перемещения</div>
        )}
      </div>
    </Modal>
  );
}

// ─── Модалка загрузки (drag-drop) ────────────────────────────────────
function UploadModal({ folderId, folderName, onClose, onDone }: { folderId: string; folderName: string; onClose: () => void; onDone: () => Promise<void> }): JSX.Element {
  const { message } = App.useApp();
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const uploadMutation = useMutation({
    // Параллельная загрузка (части в несколько потоков) — быстрее на канале с
    // задержкой + переживает большие файлы (до 1 ГБ) без 413/timeout.
    mutationFn: () => {
      setUploadPct(0);
      return diskApi.uploadParallel(folderId, file!, (p) => setUploadPct(p));
    },
    onSuccess: async () => { setUploadPct(null); await onDone(); message.success('Файл загружен'); onClose(); },
    onError: (e: Error) => { setUploadPct(null); message.error(e.message); },
  });

  return (
    <Modal
      open
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <span style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--fk-orange-soft)', color: 'var(--fk-orange)', display: 'grid', placeItems: 'center' }}><CloudUploadOutlined /></span>
          Загрузить файл
        </span>
      }
      onCancel={onClose}
      okText={uploadMutation.isPending && uploadPct !== null ? `Загрузка ${uploadPct}%` : 'Загрузить'}
      okButtonProps={{ disabled: !file, loading: uploadMutation.isPending, style: { background: 'var(--fk-orange)', borderColor: 'var(--fk-orange)' } }}
      cancelButtonProps={{ disabled: uploadMutation.isPending }}
      onOk={() => uploadMutation.mutate()}
    >
      <input ref={inputRef} type="file" style={{ display: 'none' }} onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
      {file ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', border: '1px solid var(--fk-border)', borderRadius: 'var(--fk-radius-md)', background: 'var(--fk-bg-subtle)', marginBottom: 16 }}>
          <FileType fileName={file.name} size={40} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</div>
            <div style={{ fontSize: 12, color: 'var(--fk-text-muted)' }}>{fmtSize(file.size)}</div>
          </div>
          <button type="button" className="fk-btn fk-btn-ghost fk-btn-sm" onClick={() => { setFile(null); if (inputRef.current) inputRef.current.value = ''; }}>Заменить</button>
        </div>
      ) : (
        <div
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click(); }}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) setFile(f); }}
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
            padding: '32px 16px', borderRadius: 'var(--fk-radius-md)', cursor: 'pointer', marginBottom: 16, textAlign: 'center',
            border: `1.5px dashed ${dragOver ? 'var(--fk-orange)' : 'var(--fk-border-strong)'}`,
            background: dragOver ? 'var(--fk-orange-soft)' : 'var(--fk-bg-subtle)',
            transition: 'all var(--fk-dur-fast) var(--fk-ease)',
          }}
        >
          <CloudUploadOutlined style={{ fontSize: 28, color: 'var(--fk-orange)' }} />
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--fk-text)' }}>Перетащите файл сюда или нажмите для выбора</div>
          <div style={{ fontSize: 12, color: 'var(--fk-text-muted)' }}>PDF, DOCX, XLSX, PPTX, AI, SVG, PNG, JPG, MP4 — до 1 ГБ</div>
        </div>
      )}

      <div style={{ fontSize: 12.5, color: 'var(--fk-text-2)', display: 'flex', alignItems: 'center', gap: 8 }}>
        <FolderOutlined style={{ color: 'var(--fk-orange)' }} /> Папка назначения: <b>{folderName}</b>
      </div>
    </Modal>
  );
}
